/**
 * Every browser-stored UI preference, declared once.
 *
 * Each entry in `UI_PREFS` is a key family as it is stored, with `{placeholders}` for the parts that
 * vary: `{scopeId}` for the current project (the user's id outside a project; `usePrefScope`), other
 * names for a list, table, section or record id. Existing keys keep their names: a new name wipes
 * people's choices. A new key is `sf.<area>.<name>`, then `_{scopeId}` when it belongs to a project
 * (`newPrefKey`).
 *
 * - `scope`: `project` for a project's data (record ids, filters, counts, dismissals, drafts), even
 *   when an old key has no project id in it; `browser` for pure layout, shared by every project here.
 * - `clearOnSignOut`: what signing out removes: tokens, drafts and project data such
 *   as filters and remembered views. Layout (columns, page sizes, widths, open
 *   sections, scroll) stays.
 * - `version`: the stored value's format. Every key starts at 1 (or the version its old name
 *   already carries). When old values can't be read any more, give the
 *   pref a new key with `.v<N>` on its name and register both.
 *
 * Read and write through `usePref` (hooks/use-pref.ts) or `readPref`/`writePref`: every read and
 * write is guarded, and a write that storage refuses still holds for this page until it reloads.
 * This is the only module that touches localStorage and sessionStorage (an ESLint rule).
 */

import { CHAT_DEFAULT_MODEL, CHAT_VOICE_DEFAULT_MODEL, parseChatModelSetting, type ChatModelSetting } from '@shared/chat-model';
import { STORAGE_KEYS } from '@shared/constants';
import { parsePendingSubtasks, type PendingSubtask } from '@/components/tasks/pending-subtask-codec';

export type PrefScope = 'project' | 'browser';
export type PrefStorage = 'local' | 'session';

/** The names inside `{…}` in a key template. */
export type KeyParams<K extends string> = K extends `${string}{${infer P}}${infer Rest}`
  ? P | KeyParams<Rest>
  : never;

export type PrefParams<K extends string> = { [P in KeyParams<K>]: string | null | undefined };

/** No argument for a key without placeholders, the placeholders' values otherwise. */
export type PrefKeyArgs<K extends string> = [KeyParams<K>] extends [never] ? [] : [params: PrefParams<K>];

export interface PrefDef<K extends string = string> {
  /** The key as stored, with `{placeholders}`. Never renamed: a new name wipes people's choices. */
  key: K;
  scope: PrefScope;
  storage: PrefStorage;
  version: number;
  clearOnSignOut: boolean;
  /** What it holds, and where (for anyone reading the registry). */
  holds: string;
  /**
   * For a kept pref that also holds some project data: the stored text without it (null removes the
   * key). Run at sign-out instead of removing it.
   */
  scrubOnSignOut?: (raw: string) => string | null;
  /** Work someone wrote and hasn't saved: Log out asks before sign-out removes it. */
  draft?: boolean;
}

/** A pref with its value type, for `usePref` and `readPref`/`writePref`. */
export interface Pref<T, K extends string = string> extends PrefDef<K> {
  defaultValue: T;
  /** The stored text to a value, or undefined when it can't be read (the default is used). */
  parse: (raw: string) => T | undefined;
  /** The value to store, or null to remove the key. */
  serialize: (value: T) => string | null;
}

type DefInput<K extends string> = Pick<PrefDef<K>, 'key' | 'scope' | 'clearOnSignOut' | 'holds'> &
  Partial<Pick<PrefDef<K>, 'storage' | 'version' | 'scrubOnSignOut' | 'draft'>>;

function def<const K extends string>(input: DefInput<K>): PrefDef<K> {
  return { storage: 'local', version: 1, ...input };
}

function typed<const K extends string, T>(
  input: DefInput<K> & Pick<Pref<T, K>, 'defaultValue' | 'parse' | 'serialize'>
): Pref<T, K> {
  return { storage: 'local', version: 1, ...input };
}

/** Codecs for the common shapes. */
export const prefCodec = {
  /** Text as stored; null (nothing stored) is the default. */
  text: {
    defaultValue: null as string | null,
    parse: (raw: string) => raw,
    serialize: (value: string | null) => value,
  },
  /** A flag stored as two words; anything else is the default. */
  flag(defaultValue: boolean, on = 'on', off = 'off') {
    return {
      defaultValue,
      parse: (raw: string) => (raw === on ? true : raw === off ? false : undefined),
      serialize: (value: boolean) => (value ? on : off),
    };
  },
};

/**
 * The registry. Grouped by area; ids are `<area><Name>`. List page sizes keep their helper
 * (list-page-size.ts).
 */
export const UI_PREFS = {
  // ── Auth and session (the auth flow owns these; sign-out clears the project ones) ────────────────
  authTabSession: def({ key: 'auth-storage', scope: 'project', storage: 'session', clearOnSignOut: true, holds: "This tab's user and session (zustand persist, stores/authStore.ts)." }),
  authLegacySharedSession: def({ key: 'auth-storage', scope: 'project', clearOnSignOut: true, holds: 'An old session shared by every tab; cleared on sign-out.' }),
  authLegacyToken: def({ key: STORAGE_KEYS.AUTH_TOKEN, scope: 'project', clearOnSignOut: true, holds: 'Legacy token (STORAGE_KEYS.AUTH_TOKEN); only ever removed.' }),
  authLegacyUser: def({ key: STORAGE_KEYS.USER, scope: 'project', clearOnSignOut: true, holds: 'Legacy user (STORAGE_KEYS.USER); only ever removed.' }),
  authLegacyInviteToken: def({ key: 'auth_token', scope: 'project', clearOnSignOut: true, holds: 'Legacy JWT copy from accepting an invite; cleared on sign-out.' }),
  authInProgress: def({ key: 'auth_in_progress', scope: 'browser', storage: 'session', clearOnSignOut: false, holds: 'Sign-in under way (SESSION_AUTH_KEYS); the sign-in flow clears it.' }),
  authOAuthDeparted: def({ key: 'oauth_departed_origin', scope: 'browser', storage: 'session', clearOnSignOut: false, holds: 'Left for Google mid sign-in (SESSION_AUTH_KEYS).' }),
  authRedirect: def({ key: 'auth_redirect', scope: 'browser', storage: 'session', clearOnSignOut: false, holds: 'Where to go after signing in (SESSION_AUTH_KEYS); kept so /login can return there.' }),
  appPreloadReloadAt: def({ key: 'sf.preloadReloadAt', scope: 'browser', storage: 'session', clearOnSignOut: false, holds: 'When the tab last reloaded after a deploy (index.tsx).' }),

  // ── Launch: drawn at once when the app opens, before the session check answers (lib/launch-cache.ts, T-68) ──
  launchUser: def({ key: 'sf.launch.user', scope: 'project', clearOnSignOut: true, holds: 'The last person signed in here, without their token: the app draws for them at once on the next launch (lib/launch-cache.ts).' }),
  launchData: def({ key: 'sf.launch.data', scope: 'project', clearOnSignOut: true, holds: "That person's last Inbox and projects list, shown at once on the next launch while they load again (lib/launch-cache.ts)." }),

  // ── Pickers ──────────────────────────────────────────────────────────────────────────────────
  coloursRecent: typed({ key: 'sf.colours.recent_{scopeId}', scope: 'project', clearOnSignOut: false, holds: 'Colours picked lately, newest first, for the colour picker (components/ColorField.tsx).', ...jsonCodec<string[]>([], (v) => (Array.isArray(v) ? v.filter((c): c is string => typeof c === 'string' && /^#[0-9a-f]{6}$/i.test(c)).slice(0, 8) : undefined), (v) => v.length === 0) }),

  // ── Lists ────────────────────────────────────────────────────────────────────────────────────
  listPageSize: def({ key: 'sf.listPageSize.{list}_{scopeId}', scope: 'project', clearOnSignOut: false, holds: 'Rows per page for a list (useTablePaging listId, lib/list-page-size.ts).' }),

  // ── Remembered list views (lib/url-state) ────────────────────────────────────────────────────
  urlLastView: def({ key: 'sf.lastView.{viewId}_{scopeId}', scope: 'project', clearOnSignOut: true, holds: 'A page\'s remembered filters, sort and period as a query string (lib/url-state/last-view.ts).' }),

  // ── Tasks ─────────────────────────────────────────────────────────────────────────────────────
  tasksBoardScroll: def({ key: 'sf.tasks.scroll.{scopeId}', scope: 'project', clearOnSignOut: false, holds: 'Board scroll (pages/tasks/board-scroll.ts).' }),
  tasksCollapsedColumns: def({ key: 'sf.tasks.collapsed.{scopeId}', scope: 'project', clearOnSignOut: false, holds: 'Folded Board columns (pages/tasks/board-scroll.ts).' }),
  tasksMasterFolded: typed({ key: 'sf.tasks.masterFolded_{scopeId}', scope: 'project', clearOnSignOut: false, holds: 'Folded project groups on the master board, as "<column name>:<project id>"; the scope is the user (pages/master-board).', ...jsonCodec<string[]>([], (v) => (Array.isArray(v) ? v.filter((k): k is string => typeof k === 'string') : undefined), (v) => v.length === 0) }),
  tasksSortByPriority: typed({ key: 'sf.tasks.sortByPriority', scope: 'browser', clearOnSignOut: false, holds: '"Sort by priority" in the View menu of the Board and the master Board: each column most urgent first (pages/tasks/use-priority-sort.ts, SF-T-151).', ...prefCodec.flag(true) }),
  tasksShowChanges: typed({ key: 'sf.tasks.showChanges', scope: 'browser', clearOnSignOut: false, holds: '"Show changes" in a task timeline (components/tasks/TaskTimeline.tsx).', ...prefCodec.flag(true) }),
  tasksAiSummaryOpen: typed({ key: 'sf.tasks.aiSummaryOpen', scope: 'browser', clearOnSignOut: false, holds: "Every task's AI summary open, or folded to its first sentence (components/tasks/TaskAiSummary.tsx, SF-T-305).", ...prefCodec.flag(true) }),
  tasksActivityWidth: def({ key: 'sf.tasks.activityWidth', scope: 'browser', clearOnSignOut: false, holds: 'Width of the activity pane in the Board pop-up (components/tasks/TaskDetailView.tsx).' }),
  tasksPendingSubtasks: typed({ key: 'sf.tasks.pendingSubtasks.{task}_{scopeId}', scope: 'project', clearOnSignOut: true, draft: true, holds: "Sub-tasks added to a task that the server doesn't have yet (saving, or failed), by parent task id; the scope is the user (components/tasks/pending-subtasks.ts, SF-T-144).", ...jsonCodec<PendingSubtask[]>([], parsePendingSubtasks, (v) => v.length === 0) }),
  tasksCommentDraft: typed({ key: 'sf.tasks.commentDraft.{task}_{scopeId}', scope: 'project', clearOnSignOut: true, draft: true, holds: "A task's unsent message (its mention markup), by task id; the scope is the user, so closing the Tasks panel, opening the chat or switching tasks keeps it (components/tasks/TaskChatComposer.tsx, SF-T-139).", defaultValue: '', parse: (raw: string) => raw, serialize: (v: string) => (v.trim() ? v : null) }),
  inboxNewTaskProject: typed({ key: 'sf.inbox.newTaskProject_{scopeId}', scope: 'project', clearOnSignOut: true, holds: "The project last picked for New task on the Inbox, by id; the scope is the user (pages/inbox, SF-T-135).", ...prefCodec.text }),
  tasksAttachScreenshot: def({ key: 'sf.tasks.attachScreenshot', scope: 'browser', clearOnSignOut: false, holds: '"Attach a screenshot of this page" (components/tasks/page-screenshot.ts).' }),

  chatModel: typed({ key: 'sf.chat.model_{scopeId}', scope: 'project', clearOnSignOut: false, holds: "New chat's model, effort and Fast Mode, per person (the scope is the user; pages/chat/NewChatPage.tsx, T-66).", ...jsonCodec<ChatModelSetting>(CHAT_DEFAULT_MODEL, (v) => parseChatModelSetting(v) ?? undefined) }),
  chatVoiceModel: typed({ key: 'sf.chat.voiceModel_{scopeId}', scope: 'project', clearOnSignOut: false, holds: "New chat's model, effort and Fast Mode for a chat started with voice on, per person (pages/chat/NewChatPage.tsx, T-109).", ...jsonCodec<ChatModelSetting>(CHAT_VOICE_DEFAULT_MODEL, (v) => parseChatModelSetting(v) ?? undefined) }),
  chatVoiceAutoSend: typed({ key: 'sf.chat.voiceAutoSend', scope: 'browser', clearOnSignOut: false, holds: '"Send when I stop talking" for voice in Chat (pages/chat/ChatComposer.tsx).', ...prefCodec.flag(true) }),
  chatVoiceTalkOver: typed({ key: 'sf.chat.voiceTalkOver', scope: 'browser', clearOnSignOut: false, holds: '"Talk over answers" for voice in Chat (pages/chat/ChatComposer.tsx).', ...prefCodec.flag(false) }),
  chatVoiceReadAloud: typed({ key: 'sf.chat.voiceReadAloud', scope: 'browser', clearOnSignOut: false, holds: '"Read answers aloud" for voice in Chat (pages/chat/ChatComposer.tsx).', ...prefCodec.flag(true) }),
  chatPanelOpen: typed({ key: 'sf.chat.panelOpen', scope: 'browser', clearOnSignOut: false, holds: 'Whether the chat panel beside other pages is open (pages/chat/ChatDock.tsx, T-78).', ...prefCodec.flag(false) }),
  chatPanelSession: typed({ key: 'sf.chat.panelSession_{scopeId}', scope: 'project', clearOnSignOut: true, holds: "The chat open in the chat panel, or none for New chat; the scope is the user (pages/chat/ChatDock.tsx, T-78).", ...prefCodec.text }),
  chatDraft: typed({ key: 'sf.chat.draft.{chat}_{scopeId}', scope: 'project', clearOnSignOut: true, draft: true, holds: "A chat's unsent message, by session id or `new` for New chat; the scope is the user (pages/chat/ChatSessionPage.tsx and NewChatPage.tsx, T-78).", defaultValue: '', parse: (raw: string) => raw, serialize: (v: string) => (v.trim() ? v : null) }),
  chatPanelWidth: typed({ key: 'sf.chat.panelWidth', scope: 'browser', clearOnSignOut: false, holds: 'Width of the chat panel beside other pages, in px (pages/chat/ChatDock.tsx, T-78).', defaultValue: 420, parse: (raw: string) => { const n = Number(raw); return Number.isFinite(n) && n >= 320 ? Math.round(n) : undefined; }, serialize: (n: number) => String(n) }),

  // ── Layout ────────────────────────────────────────────────────────────────────────────────────
  appPageScroll: def({ key: 'sf.page-scroll', scope: 'browser', storage: 'session', clearOnSignOut: false, holds: "Window scroll per history entry, this tab only (hooks/use-page-scroll.ts)." }),
} as const satisfies Record<string, PrefDef>;

export type UiPrefId = keyof typeof UI_PREFS;

// ── Keys ─────────────────────────────────────────────────────────────────────────────────────────

const PLACEHOLDER = /\{([^}]+)\}/g;

/**
 * The stored key for these values, or null while one is missing (no project yet): nothing is read
 * or written then.
 */
export function prefKey<K extends string>(pref: PrefDef<K>, ...args: PrefKeyArgs<K>): string | null {
  const params = (args[0] ?? {}) as Record<string, string | null | undefined>;
  let missing = false;
  const key = pref.key.replace(PLACEHOLDER, (_, name: string) => {
    const value = params[name];
    if (!value) missing = true;
    return value ?? '';
  });
  return missing ? null : key;
}

/**
 * The key for a new pref: `sf.<area>.<name>`, with `.v<N>` from version 2, then `_{scopeId}` when
 * it belongs to a project. Old keys keep their names.
 */
export function newPrefKey(area: string, name: string, options: { project: boolean; version?: number }): string {
  const version = options.version && options.version > 1 ? `.v${options.version}` : '';
  return `sf.${area}.${name}${version}${options.project ? '_{scopeId}' : ''}`;
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const matchers = new Map<string, RegExp>();

/** A key template as a pattern: a scope id has no `_`, `.` or `:`; other parts are anything. */
function keyPattern(template: string): RegExp {
  let pattern = matchers.get(template);
  if (!pattern) {
    const parts = template.split(PLACEHOLDER);
    // split with a capture group alternates literal text and placeholder names.
    const source = parts
      .map((part, i) => (i % 2 === 0 ? escapeRegExp(part) : part === 'scopeId' ? '[A-Za-z0-9-]+' : '.+'))
      .join('');
    pattern = new RegExp(`^${source}$`);
    matchers.set(template, pattern);
  }
  return pattern;
}

/** The registered prefs a stored key belongs to, in that storage. */
export function findPrefs(key: string, storage: PrefStorage = 'local'): PrefDef[] {
  return Object.values(UI_PREFS as Record<string, PrefDef>).filter(
    (pref) => pref.storage === storage && keyPattern(pref.key).test(key)
  );
}

// ── Reading and writing ──────────────────────────────────────────────────────────────────────────

type KeyStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>;

/** The browser's storage, or null where there is none (Node in tests) or it throws (some private modes). */
export function browserStorage(kind: PrefStorage): KeyStore | null {
  try {
    const store = kind === 'session' ? globalThis.sessionStorage : globalThis.localStorage;
    return store ?? null;
  } catch {
    return null;
  }
}

/** Writes storage refused (full or blocked): they hold for this page until it reloads. */
const unsaved = new Map<string, string | null>();
const listeners = new Map<string, Set<() => void>>();
const slot = (kind: PrefStorage, key: string) => `${kind}:${key}`;

function notify(kind: PrefStorage, key: string): void {
  for (const listener of listeners.get(slot(kind, key)) ?? []) listener();
}

let watchingOtherTabs = false;

function watchOtherTabs(): void {
  if (watchingOtherTabs || typeof window === 'undefined' || !window.addEventListener) return;
  watchingOtherTabs = true;
  window.addEventListener('storage', (event) => {
    const kind: PrefStorage | null =
      event.storageArea === browserStorage('local') ? 'local' : event.storageArea === browserStorage('session') ? 'session' : null;
    if (!kind) return;
    if (event.key === null) {
      // Another tab cleared everything.
      for (const id of listeners.keys()) if (id.startsWith(`${kind}:`)) notify(kind, id.slice(kind.length + 1));
      return;
    }
    unsaved.delete(slot(kind, event.key));
    notify(kind, event.key);
  });
}

/** Calls `listener` when the key changes here or in another tab; returns the unsubscribe. */
export function subscribePrefKey(kind: PrefStorage, key: string | null, listener: () => void): () => void {
  if (!key) return () => {};
  watchOtherTabs();
  const id = slot(kind, key);
  let set = listeners.get(id);
  if (!set) listeners.set(id, (set = new Set()));
  set.add(listener);
  return () => {
    set.delete(listener);
    if (!set.size) listeners.delete(id);
  };
}

/** The stored text, or null. Never throws. */
export function readRawPref(kind: PrefStorage, key: string | null): string | null {
  if (!key) return null;
  const id = slot(kind, key);
  if (unsaved.has(id)) return unsaved.get(id) ?? null;
  try {
    return browserStorage(kind)?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

/**
 * Stores the text (null removes the key) and tells this tab's readers. Never throws. False when
 * storage refused it (held for this page only) or there is no key.
 */
export function writeRawPref(kind: PrefStorage, key: string | null, raw: string | null): boolean {
  if (!key) return false;
  const id = slot(kind, key);
  let stored = true;
  try {
    const store = browserStorage(kind);
    if (!store) throw new Error('No storage');
    if (raw === null) store.removeItem(key);
    else store.setItem(key, raw);
    unsaved.delete(id);
  } catch {
    // Full or blocked: keep it for this page, so the choice still shows until a reload.
    unsaved.set(id, raw);
    stored = false;
  }
  notify(kind, key);
  return stored;
}

export function parsePref<T>(pref: Pref<T>, raw: string | null): T {
  if (raw === null) return pref.defaultValue;
  try {
    const value = pref.parse(raw);
    return value === undefined ? pref.defaultValue : value;
  } catch {
    return pref.defaultValue;
  }
}

/** The value for these placeholders; the default while one is missing or nothing is stored. */
export function readPref<T, K extends string>(pref: Pref<T, K>, ...args: PrefKeyArgs<K>): T {
  return parsePref(pref, readRawPref(pref.storage, prefKey(pref, ...args)));
}

/**
 * Stores the value for these placeholders; does nothing while one is missing. False when it
 * wasn't stored (no key yet, or storage refused it and holds it for this page only).
 */
export function writePref<T, K extends string>(pref: Pref<T, K>, value: T, ...args: PrefKeyArgs<K>): boolean {
  return writeRawPref(pref.storage, prefKey(pref, ...args), pref.serialize(value));
}

// ── Sign-out ─────────────────────────────────────────────────────────────────────────────────────

function storedKeys(store: KeyStore): string[] {
  const keys: string[] = [];
  for (let i = 0; i < store.length; i++) {
    const key = store.key(i);
    if (key !== null) keys.push(key);
  }
  return keys;
}

/**
 * Whether sign-out would remove a draft (`draft: true`) kept in this browser, from any page, not
 * only the one open: Log out asks first.
 */
export function hasStoredDrafts(
  stores: Partial<Record<PrefStorage, KeyStore | null>> = { local: browserStorage('local'), session: browserStorage('session') }
): boolean {
  // Drafts storage refused (full or blocked) live only in this page, and sign-out drops them too.
  for (const [id, raw] of unsaved) {
    if (raw === null) continue;
    const kind: PrefStorage = id.startsWith('local:') ? 'local' : 'session';
    if (findPrefs(id.slice(kind.length + 1), kind).some((pref) => pref.draft && pref.clearOnSignOut)) return true;
  }
  for (const kind of ['local', 'session'] as const) {
    const store = stores[kind];
    if (!store) continue;
    try {
      if (storedKeys(store).some((key) => findPrefs(key, kind).some((pref) => pref.draft && pref.clearOnSignOut))) return true;
    } catch {
      // Blocked storage holds no drafts.
    }
  }
  return false;
}

/**
 * Removes every pref marked `clearOnSignOut`, for every project, from this browser's localStorage and
 * this tab's sessionStorage, and scrubs the project data out of kept prefs that hold
 * some. Called by the tab that signs out and by every tab that hears it (each tab has its own
 * sessionStorage). Returns the keys removed or changed.
 */
export function clearPrefsOnSignOut(
  stores: Partial<Record<PrefStorage, KeyStore | null>> = { local: browserStorage('local'), session: browserStorage('session') }
): string[] {
  const removed: string[] = [];
  for (const kind of ['local', 'session'] as const) {
    const store = stores[kind];
    for (const id of [...unsaved.keys()]) {
      const key = id.slice(kind.length + 1);
      if (id.startsWith(`${kind}:`) && findPrefs(key, kind).some((pref) => pref.clearOnSignOut)) {
        unsaved.delete(id);
        notify(kind, key);
      }
    }
    if (!store) continue;
    let keys: string[];
    try {
      keys = storedKeys(store);
    } catch {
      continue;
    }
    for (const key of keys) {
      const matches = findPrefs(key, kind);
      try {
        if (matches.some((pref) => pref.clearOnSignOut)) {
          store.removeItem(key);
          removed.push(key);
          notify(kind, key);
          continue;
        }
        const scrub = matches.find((pref) => pref.scrubOnSignOut)?.scrubOnSignOut;
        const raw = scrub ? store.getItem(key) : null;
        if (!scrub || raw === null) continue;
        let next: string | null;
        try {
          next = scrub(raw);
        } catch {
          next = null; // Unreadable: it can't be trusted to hold no project data.
        }
        if (next === raw) continue;
        if (next === null) store.removeItem(key);
        else store.setItem(key, next);
        removed.push(key);
        notify(kind, key);
      } catch {
        // Blocked storage: nothing more to do.
      }
    }
  }
  return removed;
}

// ── Codecs for registered keys ─────────────────────────────────────────────────────────────────────
// The `def` entries above have no value type. A module moving onto readPref/writePref/usePref gives
// its entry one here, beside the code that understands the value, so the registry lines (which open
// PRs edit) stay as they are.

type PrefCodec<T> = Pick<Pref<T>, 'defaultValue' | 'parse' | 'serialize'>;

/** A registered key family with its value type: `readPref(withCodec(UI_PREFS.x, prefCodec.text), …)`. */
export function withCodec<const K extends string, T>(pref: PrefDef<K>, codec: PrefCodec<T>): Pref<T, K> {
  return { ...pref, ...codec };
}

/**
 * JSON. `read` turns the parsed value into one the page can use, or undefined for the default
 * (unreadable JSON is the default too). `removeWhen`: values stored as no key at all.
 */
export function jsonCodec<T>(
  defaultValue: T,
  read: (value: unknown) => T | undefined,
  removeWhen?: (value: T) => boolean
): PrefCodec<T> {
  return {
    defaultValue,
    parse: (raw) => read(JSON.parse(raw)),
    serialize: (value) => (removeWhen?.(value) ? null : JSON.stringify(value)),
  };
}

/** A marker: 'true' while it's set, no key at all otherwise (the sign-in markers). */
export const markerCodec: PrefCodec<boolean> = {
  defaultValue: false,
  parse: (raw) => raw === 'true',
  serialize: (value) => (value ? 'true' : null),
};

/** Removes the key for these placeholders (a `def` entry needs no codec for this). */
export function removePref<K extends string>(pref: PrefDef<K>, ...args: PrefKeyArgs<K>): void {
  writeRawPref(pref.storage, prefKey(pref, ...args), null);
}

/**
 * A zustand `persist` storage on these helpers (the auth store): reads and writes never
 * throw, and a write storage refuses holds for this page until it reloads. Its key must be
 * registered like any other.
 */
export function prefStateStorage(kind: PrefStorage) {
  return {
    getItem: (key: string) => readRawPref(kind, key),
    setItem: (key: string, value: string) => void writeRawPref(kind, key, value),
    removeItem: (key: string) => void writeRawPref(kind, key, null),
  };
}

/** Tests only: forget writes held in memory and every listener. */
export function resetPrefsForTests(): void {
  unsaved.clear();
  listeners.clear();
}
